import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ArrowDown,
    ArrowUp,
    ChevronDown,
    Search,
    SlidersHorizontal,
    X,
} from "lucide-react";
import { twMerge } from "tailwind-merge";
import useDebounce from "../hooks/use-debounce";
import {
    activeGroupCount,
    countFacet,
    emptyFilters,
    facetOptions,
    filterCrumbs,
    getFacetGroup,
    hasActiveFilters,
    sortsForTab,
    toggleFilterValue,
} from "../utils/event-fighter-filters";

// Facet layout: one row per line, a fixed-width micro-label on the left.
// Rows 3 and 4 pair two groups up so the panel stays four lines tall.
const FACET_ROWS = [["entry"], ["wc"], ["gender", "age"], ["bout", "flag"]];

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Toolbar + collapsible facet panel for the admin Event Fighters list.
 *
 * Fully controlled: it owns nothing but the panel's open/closed state and the
 * un-debounced text in the search box. Everything else comes down as props and
 * goes back up through `onChange` / `onSortChange`.
 *
 * @param {object}   props
 * @param {object}   props.filters       current filter state (see utils/event-fighter-filters)
 * @param {Function} props.onChange      (nextFilters) => void
 * @param {string}   props.sort          active sort key
 * @param {"asc"|"desc"} props.order     active sort direction
 * @param {Function} props.onSortChange  (sortKey, order) => void
 * @param {Array}    props.pool          the tab's unfiltered rows — fighters, or bouts on `matched`
 * @param {*}        props.eventDate     event.dateTime, for ages and the minor flag
 * @param {string}   props.tab           all | matched | pulledOut | refunded
 * @param {number}   props.shownCount    rows after filtering
 * @param {number}   props.totalCount    rows on this tab in total
 */
export default function EventFighterFilterBar({
    filters,
    onChange,
    sort,
    order,
    onSortChange,
    pool,
    eventDate,
    tab,
    shownCount,
    totalCount,
}) {
    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState(filters?.q ?? "");
    const debouncedSearch = useDebounce(searchText, SEARCH_DEBOUNCE_MS);

    // Refs so the debounce effect can fire on the debounced value alone,
    // without re-running every time the parent hands down new filters.
    const onChangeRef = useRef(onChange);
    const filtersRef = useRef(filters);
    const lastPushedRef = useRef(filters?.q ?? "");
    onChangeRef.current = onChange;
    filtersRef.current = filters;

    // Push the debounced search term up.
    useEffect(() => {
        if (debouncedSearch === lastPushedRef.current) return;
        lastPushedRef.current = debouncedSearch;
        onChangeRef.current?.({
            ...filtersRef.current,
            q: debouncedSearch,
        });
    }, [debouncedSearch]);

    // Pull it back down when the parent resets it (Clear filters, a URL load).
    useEffect(() => {
        const q = filters?.q ?? "";
        if (q !== lastPushedRef.current) {
            lastPushedRef.current = q;
            setSearchText(q);
        }
    }, [filters?.q]);

    const isPairTab = tab === "matched";
    const noun = isPairTab ? "bouts" : "fighters";
    const facetOpts = useMemo(
        () => ({ eventDate, pairs: isPairTab }),
        [eventDate, isPairTab]
    );

    const sortOptions = sortsForTab(tab);
    const activeCount = activeGroupCount(filters);
    const anyActive = hasActiveFilters(filters);
    const crumbs = filterCrumbs(filters);

    const handleClear = () => onChange?.(emptyFilters());
    const handleToggle = (groupKey, value) =>
        onChange?.(toggleFilterValue(filters, groupKey, value));

    const renderGroup = (groupKey) => {
        const group = getFacetGroup(groupKey);
        if (!group) return null;
        const options = facetOptions(groupKey, pool, facetOpts);
        if (!options.length) return null;
        const selected = filters?.[groupKey] ?? [];

        return (
            <div
                key={groupKey}
                className="flex flex-row flex-wrap items-start gap-2 min-w-0"
            >
                <span className="w-14 shrink-0 pt-1 text-[10px] font-bold uppercase tracking-wider opacity-60">
                    {group.label}
                </span>
                <div className="flex flex-row flex-wrap gap-1">
                    {options.map((option) => {
                        const isOn = selected.includes(option.value);
                        const count = countFacet(
                            pool,
                            filters,
                            groupKey,
                            option.value,
                            facetOpts
                        );
                        const isDead = count === 0 && !isOn;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                disabled={isDead}
                                aria-pressed={isOn}
                                className={twMerge(
                                    "btn btn-xs gap-1 font-normal normal-case",
                                    isOn
                                        ? "btn-primary"
                                        : "btn-ghost border border-base-300",
                                    isDead &&
                                        "opacity-30 pointer-events-none cursor-default"
                                )}
                                onClick={() =>
                                    handleToggle(groupKey, option.value)
                                }
                            >
                                {option.label}
                                <span className="text-[10px] opacity-60 font-mono">
                                    {count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full flex flex-col gap-2">
            {/* Toolbar — one compact row, wraps on narrow screens */}
            <div className="flex flex-row flex-wrap items-center gap-2">
                {/* The placeholder names the two fields people actually type
                    into; the title spells out everything the search covers,
                    so a long placeholder can't clip in a narrow panel. */}
                <label
                    className="input input-bordered input-sm flex flex-1 min-w-[9rem] items-center gap-2"
                    title="Searches name, gym / team, nationality and IC / passport"
                >
                    <Search size={14} className="opacity-60 shrink-0" />
                    <input
                        type="text"
                        className="grow min-w-0 bg-transparent text-sm"
                        placeholder="Search name or gym…"
                        value={searchText}
                        autoComplete="off"
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </label>

                <div className="flex h-8 flex-row items-center gap-1 border border-base-300 px-2">
                    <span className="text-xs opacity-60">Sort</span>
                    <select
                        aria-label="Sort by"
                        className="cursor-pointer appearance-none bg-transparent pr-1 text-xs font-medium focus:outline-none"
                        value={sort}
                        onChange={(e) => onSortChange?.(e.target.value, order)}
                    >
                        {sortOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="opacity-60 shrink-0" />
                </div>

                <button
                    type="button"
                    className="btn btn-sm btn-square btn-ghost border border-base-300"
                    title={order === "asc" ? "Ascending" : "Descending"}
                    aria-label="Toggle sort direction"
                    onClick={() =>
                        onSortChange?.(sort, order === "asc" ? "desc" : "asc")
                    }
                >
                    {order === "asc" ? (
                        <ArrowUp size={14} />
                    ) : (
                        <ArrowDown size={14} />
                    )}
                </button>

                <button
                    type="button"
                    aria-expanded={open}
                    className={twMerge(
                        "btn btn-sm gap-1 normal-case",
                        open ? "btn-primary" : "btn-ghost border border-base-300"
                    )}
                    onClick={() => setOpen((prev) => !prev)}
                >
                    <SlidersHorizontal size={14} />
                    Filters
                    {activeCount > 0 && (
                        <span
                            className={twMerge(
                                "badge badge-sm font-mono",
                                open
                                    ? "bg-black text-white border-black"
                                    : "bg-white text-black border-white"
                            )}
                        >
                            {activeCount}
                        </span>
                    )}
                </button>

                {anyActive && (
                    <button
                        type="button"
                        className="btn btn-sm btn-square btn-ghost"
                        title="Clear all filters"
                        aria-label="Clear all filters"
                        onClick={handleClear}
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {/* Facet panel */}
            {open && (
                <div className="flex flex-col gap-2 border border-base-300 bg-base-200 p-2">
                    {FACET_ROWS.map((row, index) => {
                        const rendered = row
                            .map((groupKey) => renderGroup(groupKey))
                            .filter(Boolean);
                        if (!rendered.length) return null;
                        return (
                            <div
                                key={index}
                                className="flex flex-row flex-wrap items-start gap-x-6 gap-y-2"
                            >
                                {rendered}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Result meta */}
            <div className="flex flex-row flex-wrap items-center justify-between gap-2 text-xs opacity-70">
                <span>
                    Showing <strong>{shownCount}</strong> of{" "}
                    <strong>{totalCount}</strong> {noun}
                </span>
                {crumbs.length > 0 && (
                    <div className="flex flex-row flex-wrap gap-1">
                        {crumbs.map((crumb, index) => (
                            <span
                                key={index}
                                className="badge badge-sm badge-outline whitespace-nowrap"
                            >
                                {crumb}
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
